import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { postsAPI } from '../services/api';

const HomePage = () => {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await postsAPI.getAll();
        setPosts(response.data);
      } catch (err) {
        console.error('Failed to fetch posts:', err);
      }
    };
    fetchPosts();
  }, []);

  const getPostIdByTitle = (title) => {
    const post = posts.find(p => p.title.toLowerCase().includes(title.toLowerCase()));
    return post ? post.post_id : null;
  };

  const AttractionCard = ({ title, category, description, location, rating, image }) => {
    const postId = getPostIdByTitle(title);
    const linkTo = postId ? `/posts/${postId}` : '/explore';

    return (
      <div className="group bg-surface-container-lowest rounded-xl overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500 flex flex-col">
        <Link to={linkTo} className="flex flex-col flex-grow">
          <div className="h-64 overflow-hidden relative">
            <img 
              alt={title} 
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" 
              src={image}
            />
            <div className="absolute top-4 right-4 bg-white/90 backdrop-blur px-3 py-1 rounded-full text-xs font-bold text-primary flex items-center gap-1 shadow-sm">
              <span className="material-symbols-outlined text-sm" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
              {rating}
            </div>
          </div>
          <div className="p-8 flex-grow">
            <span className="text-tertiary text-xs font-bold uppercase tracking-widest mb-2 block">{category}</span>
            <h3 className="text-xl font-bold mb-2 group-hover:text-primary transition-colors">{title}</h3>
            <p className="text-on-surface-variant text-sm leading-relaxed mb-6">{description}</p>
            <div className="flex items-center gap-2 text-on-surface-variant/60 text-xs mt-auto">
              <span className="material-symbols-outlined text-sm">location_on</span>
              {location}
            </div>
          </div>
        </Link>
      </div>
    );
  };

  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="relative min-h-[870px] flex items-center px-8 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-transparent z-10"></div>
          <img 
            alt="Cityscape neighborhood" 
            className="w-full h-full object-cover" 
            src="https://lh3.googleusercontent.com/aida-public/AB6AXuAqzcTJXa80LQtolvpqM2ffc7Q28kSrBdz1pxjsLhJbie9BQWMVP2uXbE97qWtIfptcM7Zs8Fb6XqvzefpQ5CqrNu8lgdvpIWynsYghwOW2jsUS4fs3eEaBEMB2JnjlubsYt2AkTvUD2K-4thhIm1uobHHdqjPGECwAg9amIKb1IcUShUEQKEh_sK9oreC0ELtj6I_ImXe26ETihSPwqBR57mU3GtjIbHVRQKtgDFcqvszkYf4msgiftco6syPIhtLd_kjFP-F5W1s"
          />
        </div>
        <div className="relative z-20 max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <h1 className="text-[3.5rem] font-bold leading-[1.1] tracking-tight mb-8 text-on-background">
              Discover the <span className="text-primary">Soul</span> of Your Neighborhood.
            </h1>
            <p className="text-xl text-on-surface-variant max-w-xl mb-10 leading-relaxed">
              An editorial guide to local living. We curate the finest spots, the most promising jobs, and the hidden gems that make a house feel like home.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/explore" className="bg-primary text-on-primary px-10 py-4 rounded-full font-bold text-lg shadow-xl shadow-primary/25 hover:brightness-110 transition-all active:scale-95 flex items-center justify-center">
                Explore Your Neighborhood
              </Link>
              <Link to="/housing" className="px-10 py-4 rounded-full font-bold text-lg text-primary bg-surface-container-lowest border border-outline-variant/20 shadow-sm hover:bg-surface-container-low transition-all active:scale-95 flex items-center justify-center">
                View Housing
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Gems (Editorial Grid) */}
      <section className="max-w-7xl mx-auto px-8 py-24 w-full">
        <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6">
          <div className="max-w-2xl">
            <h2 className="text-4xl font-bold mb-4">Discover Bucharest</h2>
            <p className="text-on-surface-variant text-lg">Essential landmarks and cultural treasures from the heart of Romania. Experience the "Little Paris" through its most iconic spots.</p>
          </div>
          <Link to="/explore" className="text-primary font-bold flex items-center gap-2 group">
            See all attractions 
            <span className="material-symbols-outlined group-hover:translate-x-1 transition-transform">arrow_forward</span>
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <AttractionCard 
            title="Palace of the Parliament"
            category="History & Architecture"
            description="A colossal symbol of the communist era, this is the world's second-largest administrative building and a must-see for its sheer scale."
            location="Centru • District 5"
            rating="4.8"
            image="https://images.unsplash.com/photo-1555662800-92f44b37a43d?auto=format&fit=crop&w=1200&q=80"
          />
          <AttractionCard 
            title="Romanian Athenaeum"
            category="Culture & Arts"
            description="The city's most prestigious concert hall, famous for its ornate dome and exceptional acoustics. The heart of classical music."
            location="Calea Victoriei • District 1"
            rating="5.0"
            image="https://images.unsplash.com/photo-1584713503693-bb386ec95cf2?auto=format&fit=crop&w=1200&q=80"
          />
          <AttractionCard 
            title="Village Museum"
            category="Heritage & Outdoors"
            description="An open-air ethnographic museum showcasing 300+ authentic traditional buildings reconstructing the rural past of Romania."
            location="Herăstrău Park • District 1"
            rating="4.7"
            image="https://images.unsplash.com/photo-1527631746610-bca00a040d60?auto=format&fit=crop&w=1200&q=80"
          />
        </div>
      </section>

      {/* Bento Sections: Housing & Jobs */}
      <section className="max-w-7xl mx-auto px-8 py-24 w-full">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          {/* Housing Search Preview */}
          <div className="md:col-span-7 bg-surface-container-low rounded-xl p-12 relative overflow-hidden group">
            <div className="relative z-10 max-w-md">
              <h2 className="text-4xl font-bold mb-6">Housing Curated</h2>
              <p className="text-on-surface-variant text-lg mb-8">Skip the endless scrolling. We list vetted apartments and homes that match your lifestyle.</p>
              <div className="flex flex-wrap gap-3 mb-12">
                <span className="bg-primary-fixed text-on-primary-fixed px-4 py-1.5 rounded-full text-sm font-semibold">Pet Friendly</span>
                <span className="bg-primary-fixed text-on-primary-fixed px-4 py-1.5 rounded-full text-sm font-semibold">Work from Home</span>
                <span className="bg-primary-fixed text-on-primary-fixed px-4 py-1.5 rounded-full text-sm font-semibold">Studio Loft</span>
              </div>
              <Link to="/housing" className="bg-primary text-on-primary px-8 py-3 rounded-full font-bold shadow-lg hover:scale-105 transition-all inline-block">
                Browse Listings
              </Link>
            </div>
            <div className="absolute -right-20 -bottom-20 w-96 h-96 bg-primary-container/10 rounded-full blur-3xl group-hover:bg-primary-container/20 transition-all duration-700"></div>
            <span className="absolute right-12 top-12 material-symbols-outlined text-[12rem] text-primary/5 select-none">home</span>
          </div>
          {/* Job Board Preview */}
          <div className="md:col-span-5 bg-secondary-container text-on-secondary-container rounded-xl p-12 relative overflow-hidden group">
            <div className="relative z-10">
              <h2 className="text-3xl font-bold mb-4">Local Careers</h2>
              <p className="text-on-secondary-container/80 text-lg mb-8">Direct connections to the best local companies and startups.</p>
              <div className="space-y-4 mb-10">
                <div className="bg-white/10 backdrop-blur p-4 rounded-lg flex justify-between items-center group-hover:bg-white/20 transition-all cursor-pointer">
                  <div>
                    <div className="font-bold">Creative Lead</div>
                    <div className="text-sm opacity-70">Design Studio X</div>
                  </div>
                  <span className="material-symbols-outlined">arrow_outward</span>
                </div>
                <div className="bg-white/10 backdrop-blur p-4 rounded-lg flex justify-between items-center group-hover:bg-white/20 transition-all cursor-pointer">
                  <div>
                    <div className="font-bold">Senior Barista</div>
                    <div className="text-sm opacity-70">The Roastery</div>
                  </div>
                  <span className="material-symbols-outlined">arrow_outward</span>
                </div>
              </div>
              <Link to="/jobs" className="w-full bg-white text-secondary font-bold py-3 rounded-full hover:shadow-xl transition-all inline-block text-center">
                View Job Board
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Join Section */}
      <section className="bg-surface-container-low py-32 w-full">
        <div className="max-w-7xl mx-auto px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-4xl font-bold mb-6">The Community Advantage</h2>
            <p className="text-on-surface-variant text-xl">LocalGuide is more than a directory. It's a curated ecosystem designed for meaningful connection.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-16">
            <div className="text-center group">
              <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-8 group-hover:scale-110 transition-transform duration-300">
                <span className="material-symbols-outlined text-primary text-4xl">verified_user</span>
              </div>
              <h3 className="text-xl font-bold mb-4">Vetted Content</h3>
              <p className="text-on-surface-variant leading-relaxed">Every spot, job, and house is reviewed by our community experts to ensure quality and safety.</p>
            </div>
            <div className="text-center group">
              <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-8 group-hover:scale-110 transition-transform duration-300">
                <span className="material-symbols-outlined text-primary text-4xl">groups</span>
              </div>
              <h3 className="text-xl font-bold mb-4">True Connection</h3>
              <p className="text-on-surface-variant leading-relaxed">Meet neighbors who share your interests through organized meetups and focused community boards.</p>
            </div>
            <div className="text-center group">
              <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-8 group-hover:scale-110 transition-transform duration-300">
                <span className="material-symbols-outlined text-primary text-4xl">local_activity</span>
              </div>
              <h3 className="text-xl font-bold mb-4">Exclusive Access</h3>
              <p className="text-on-surface-variant leading-relaxed">Get early bird invites to local openings and special discounts at our Featured Gem partners.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter / CTA */}
      <section className="max-w-7xl mx-auto px-8 py-24 w-full">
        <div className="bg-primary rounded-[2rem] p-16 text-center text-on-primary relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full blur-[100px]"></div>
            <div className="absolute bottom-0 right-0 w-64 h-64 bg-white rounded-full blur-[100px]"></div>
          </div>
          <div className="relative z-10 max-w-2xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to find your place?</h2>
            <p className="text-xl mb-10 text-on-primary-container opacity-90">Join 50,000+ neighbors who rely on LocalGuide for their daily community discovery.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <input 
                className="px-8 py-4 rounded-full bg-white/20 border-white/30 backdrop-blur-md text-white placeholder:text-white/60 focus:ring-2 focus:ring-white/50 border-none outline-none w-full sm:w-80 font-body" 
                placeholder="Enter your email" 
                type="email"
              />
              <button className="bg-white text-primary px-10 py-4 rounded-full font-bold hover:scale-105 active:scale-95 transition-all shadow-xl">Get Started</button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
